import React, { useContext, useMemo } from "react";
import { useIntl } from "react-intl";
import { toast } from "sonner";

import { AuthContext } from "context";
import { useUpdateProfilePreferences } from "hooks/profile";
import { Switch } from "components/ui/switch";
import { Label } from "components/ui/label";

export const MESSAGING_EMAIL_ALERT = "messaging";

const wantsMessagingEmail = (emailAlerts: Array<string> | null | undefined): boolean =>
    emailAlerts == null || emailAlerts.includes(MESSAGING_EMAIL_ALERT);

const MessagingEmailAlertsForm: React.FC = () => {
    const intl = useIntl();
    const { account, updateAccount } = useContext(AuthContext);
    const updatePreferences = useUpdateProfilePreferences();

    const enabled = useMemo(
        () => wantsMessagingEmail(account?.user.email_alerts),
        [account?.user.email_alerts],
    );

    const userId = account?.user.id;
    const isPending = updatePreferences.isPending;

    const handleCheckedChange = async (checked: boolean) => {
        if (!userId || isPending) {
            return;
        }

        const currentAlerts = account?.user.email_alerts ?? [];
        const nextAlerts = checked
            ? Array.from(new Set([...currentAlerts, MESSAGING_EMAIL_ALERT]))
            : currentAlerts.filter((alert) => alert !== MESSAGING_EMAIL_ALERT);

        try {
            await updatePreferences.mutateAsync({
                id: userId,
                data: { email_alerts: nextAlerts },
            });
            await updateAccount();
            toast.success(intl.formatMessage({ id: "profile.settings.notifications.updated" }));
        } catch {
            toast.error(intl.formatMessage({ id: "profile.settings.notifications.update_error" }));
        }
    };

    if (!userId) {
        return null;
    }

    const label = intl.formatMessage({ id: "profile.settings.notifications.messaging.label" });

    return (
        <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
                <Label htmlFor="messaging-email-alerts" className="text-sm font-medium text-slate-900">
                    {label}
                </Label>
                <p className="text-sm text-slate-500">
                    {intl.formatMessage({ id: "profile.settings.notifications.messaging.description" })}
                </p>
            </div>
            <Switch
                id="messaging-email-alerts"
                checked={enabled}
                disabled={isPending}
                onCheckedChange={(checked: boolean) => {
                    void handleCheckedChange(checked);
                }}
                aria-label={label}
            />
        </div>
    );
};

export default MessagingEmailAlertsForm;
